<template>
  <th @click="onSort" :class="headerClass" :style="headerStyle" v-if="!header.hide">    
    <v-menu @input="onInput" v-show="hasValue" min-width="180px" v-model="menu" :close-on-content-click="false" offset-y style="flex: 0 0;">
      <template v-slot:activator="{ on }">
        <span :class="{ 'success--text': isSliced }">{{ header.text }}</span>
        <v-icon class="arrow flex-shrink-0" :class="{'descending': descending}" v-if="sortable" small>arrow_upward</v-icon>
        <v-btn :disabled="disabled" @click.stop icon class="flex-shrink-0 ma-0" small v-on="on">
          <v-icon :color="isSliced ? 'success' : ''" small>filter_alt</v-icon>
        </v-btn>
        <v-btn v-show="isSliced" @click.stop="reset()" icon class="flex-shrink-0 ma-0" flat small>
          <v-icon color="success">close</v-icon>
        </v-btn>
      </template>
      <v-card style="display: flex; flex-direction: column;">
        <v-flex style="flex: 0 0 132px;">
          <v-card-title class="py-2 title font-weight-medium">
            <v-icon class="mr-1">filter_alt</v-icon>
            Filter values
          </v-card-title>
          <v-divider />
          <v-card-text class="py-2" style="background-color: #f7f7f7; border-bottom: 1px solid #dddddd;">
            <v-text-field v-model="search" :loading="searching" @keyup.enter="sliceCurrent()" hide-details clearable solo single-line flat style="border: 1px solid #ddd" placeholder="Search values" append-icon="search" />
            <div class="pt-2">
              <v-btn class="pa-0 mx-0 my-1" @click="setAll(true)" small>{{ $t('Select All') }}</v-btn>
              <v-btn class="pa-0 mx-0 my-1" @click="setAll(false)" small>{{ $t('Select None') }}</v-btn>
            </div>
          </v-card-text>
        </v-flex>
        <v-flex v-if="Object.keys(items).length == 0" style="flex: 1 1;">
          <v-list-tile>
            <v-list-tile-title class="align-center d-flex justify-center">
              <v-icon class="mr-1 flex-grow-0" icon="success">help_outline</v-icon>
              <div class="body-1 flex-grow-0">No values to filter</div>
            </v-list-tile-title>
          </v-list-tile>
        </v-flex>
        <v-flex ref="virtualContainer" v-else style="flex: 1 1 200px; width: 100%; min-height: 0;">
          <v-list flat class="py-0 slicer" dense>
            <ItemsScroller ref="itemsScroller" :items="updatedItems" @toggle="toggle($event)" />
          </v-list>
        </v-flex>
        <v-flex style="flex: 0 0 40px;">
          <v-layout class="px-1" v-if="slowMode">
            <v-btn block color="success" :loading="loading" :disabled="Object.keys(items).length == 0" small @click="triggerAndClose">{{$t('Button.Update')}}</v-btn>
          </v-layout>
        </v-flex>
      </v-card>
    </v-menu>
    <v-btn v-show="!hasValue" class="pa-0 ma-0" flat small>{{ header.text }}</v-btn>
  </th>
</template>

<script>
  import ItemsScroller from './ItemsScroller'
  import {
    debounce,
    defaultDebounceDelay,
  } from '@/helpers/performance'

  function makeKeyedValues(data, header) {
    let result = {}

    let key = header.value
    if (!key) return

    data.forEach(datum => {
      let value = datum[key]
      if (header.format) value = header.format(value)
      if (value instanceof Array || value instanceof Object) {
        // do nothing for now
      } else {
        result[value] = true
      }
    })

    return result
  }

  function makeValues(values) {
    let result = {}
    values.forEach(value => result[value] = true)
    return result
  }

  export default {
    components: {
      ItemsScroller,
    },
    props: {
      header: Object,
      data: Array,
      slowMode: Boolean,
      debounceTime: Number,
      disabled: Boolean,
      headerStyle: [String, Object],
      sortable: Boolean,
      active: Boolean,
      descending: Boolean,
    },
    data() {
      return {
        loading: false,
        menu: false,
        search: '',
        isSliced: false,
        isUpdated: false,
        items: {},
        filteredItems: {},
        updatedItems: {},
        searching: false,
      }
    },
    computed: {
      headerClass() {
        return {
          'column align-center d-flex max-width': true,
          'active': this.active,
          'sortable': this.sortable,
        }
      },
      align() {
        return this.header && this.header.align || 'center'
      },
      hasValue() {
        return this.header.value
      },
      onSearch() {
        return debounce(function executable(value, context) {
          let keys = Object.keys(context.updatedItems)
          let filteredItems = {}
          keys.forEach(key => {
            if (key.toLowerCase().includes(value))
              filteredItems[key] = context.updatedItems[key]
          })

          this.filteredItems = filteredItems
          if (this.$refs.itemsScroller) {
            this.$refs.itemsScroller.update(filteredItems)
              .then(() => this.searching = false)
          } else {
            this.searching = false
          }
        }, this.debounceTimer)
      },
      debounceTimer() {
        return this.slowMode ? this.debounceTime || defaultDebounceDelay : 0
      },
    },
    watch: {
      search(value) {
        this.searching = true
        this.onSearch(value ? value.toLowerCase() : '', this)
      },
      'header.hide'() {
        this.reset()
      },
    },
    methods: {
      onSort() {
        if (this.sortable) this.$emit('sort')
      },
      onInput(value) {
        if (!value && !this.isUpdated) this.updatedItems = JSON.parse(JSON.stringify(this.items))
        this.isUpdated = false
      },
      setAll(value) {
        for (var key in this.items) {
          this.updatedItems[key] = value
        }

        if (!this.slowMode) this.trigger()
      },
      get() {
        return {
          header: this.header,
          items: this.items,
        }
      },
      update() {
        let result
        if (this.header.slicerValues) {
          result = makeValues(this.header.slicerValues)
        } else {
          result = makeKeyedValues(this.data, this.header)
        }

        if (this.header.includeNull) {
          result[null] = true
        }

        this.items = result
        this.updatedItems = JSON.parse(JSON.stringify(result))
        this.filteredItems = this.updatedItems

        // If there are items, initialize virtual scroller
        this.$nextTick(() => {
          if (this.$refs.itemsScroller) {
            this.$refs.itemsScroller.initialize(this.updatedItems, this.$refs.virtualContainer)
          }
        })
      },
      toggle(key) {
        this.updatedItems[key] = !this.updatedItems[key]
        if (!this.slowMode) this.trigger()
      },
      trigger() {
        this.items = JSON.parse(JSON.stringify(this.updatedItems))
        this.isUpdated = true
        this.updateIsSliced()
        this.$emit('slice')
      },
      triggerAndClose() {
        this.trigger()
        this.menu = false
      },
      sliceCurrent() {
        this.setAll(false)
        let currentKeys = Object.keys(this.filteredItems)
        currentKeys.forEach(key => this.updatedItems[key] = true)
      },
      updateIsSliced() {
        let keys = Object.keys(this.items)
        for (let i = 0; i < keys.length; i++) {
          let key = keys[i]
          if (!this.items[key]) {
            this.isSliced = true
            return
          }
        }

        this.isSliced = false
      },
      reset() {
        this.setAll(true)
        this.trigger()
      },
    },
  }
</script>

<style>
  .slicer>div>a.v-list__tile.v-list__tile--link {
    padding: 0 8px;
  }

  .slicer {
    height: 100% !important;
    width: 100% !important;
  }
</style>
<style scoped>
  .virtualContainer {
    height: 100%;
    width: 100%;
  }

  .active>span {
    color: rgba(0, 0, 0, 0.87);
  }

  .active>.arrow {
    opacity: 1;
  }

  .sortable {
    cursor: pointer;
  }

  .sortable:hover {
    color: rgba(0, 0, 0, 0.87);
  }

  .arrow {
    opacity: 0;
  }

  .sortable:hover>.arrow {
    opacity: 0.6;
  }

  .active>.descending {
    -webkit-transform: rotate(-180deg);
    -moz-transform: rotate(-180deg);
    -o-transform: rotate(-180deg);
    transform: rotate(-180deg);
    ms-filter: "progid:DXImageTransform.Microsoft.BasicImage(rotation=2)";
    filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=2);
  }

  .active.sortable:hover>.arrow {
    opacity: 1;
  }
</style>